import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { dateToSqlDate } from '../../helpers/dateFuncs';
import fetchData from '../../helpers/fetchData';
import { formatNumber } from '../../helpers/numberUtils';
import { exportToExcel } from '../../helpers/exportFuncs';
import { FilterContext, IFilterState } from '../../contexts/FilterContext';
import { getComparator, Order, sortedRowInformation } from '../../helpers/sortingsFuncs';
import LoadingSpinner from '../LoadingSpinner';
import { visuallyHidden } from '@mui/utils';

interface RoiPerAssetTypeData {
  asset_type: string;
  leads: number;
  revenue_30d: number;
  revenue_total: number;
  total_cost: number;
}

const RoiPerAssetType: React.FC = () => {
  const { filterState } = useContext(FilterContext)!;
  const [data, setData] = useState<RoiPerAssetTypeData[]>([]);
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<keyof RoiPerAssetTypeData>('leads');

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof RoiPerAssetTypeData,
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const fetchRoiData = async (filterState: IFilterState): Promise<RoiPerAssetTypeData[]> => {
    if (!filterState.startDate || !filterState.endDate || !filterState.network) {
      return [];
    }

    const networks = Array.isArray(filterState.network)
      ? filterState.network
      : [filterState.network];
    const themes = Array.isArray(filterState.theme)
      ? filterState.theme
      : filterState.theme
        ? [filterState.theme]
        : [];
    const params = {
      start_date: dateToSqlDate(filterState.startDate),
      end_date: dateToSqlDate(filterState.endDate),
      network: networks,
      ...(themes.length > 0 && { theme: themes }),
      ...(filterState.vnrVerlag.length > 0 && { verlag: filterState.vnrVerlag }),
    };

    return fetchData<RoiPerAssetTypeData>('/api/vnr/roi-per-asset-type', params) as Promise<
      RoiPerAssetTypeData[]
    >;
  };

  useEffect(() => {
    const loadData = async () => {
      const hasNetwork = Array.isArray(filterState.network)
        ? filterState.network.length > 0
        : !!filterState.network;

      if (!hasNetwork || !filterState.startDate || !filterState.endDate) {
        return;
      }

      setLoading(true);
      try {
        const result = await fetchRoiData(filterState);
        const processedResult = result.map((item) => ({
          ...item,
          leads: parseInt(String(item.leads), 10) || 0,
          revenue_30d: parseFloat(String(item.revenue_30d)) || 0,
          revenue_total: parseFloat(String(item.revenue_total)) || 0,
          total_cost: parseFloat(String(item.total_cost)) || 0,
        }));
        setData(processedResult);
      } catch (error) {
        console.error('Error fetching ROI by asset type data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [
    filterState.network,
    filterState.startDate,
    filterState.endDate,
    filterState.theme,
    filterState.vnrVerlag,
  ]);

  const computeSummary = (data: RoiPerAssetTypeData[]): RoiPerAssetTypeData => {
    if (!data.length) {
      return {
        asset_type: 'Insgesamt',
        leads: 0,
        revenue_30d: 0,
        revenue_total: 0,
        total_cost: 0,
      };
    }

    const summary = {
      asset_type: 'Insgesamt',
      leads: 0,
      revenue_30d: 0,
      revenue_total: 0,
      total_cost: 0,
    };

    data.forEach((item) => {
      summary.leads += parseInt(String(item.leads), 10) || 0;
      summary.revenue_30d += parseFloat(String(item.revenue_30d)) || 0;
      summary.revenue_total += parseFloat(String(item.revenue_total)) || 0;
      summary.total_cost += parseFloat(String(item.total_cost)) || 0;
    });

    return summary;
  };

  const summary = computeSummary(data);
  const sortedData = sortedRowInformation(data, getComparator(order, orderBy));
  const allData = [...data, summary];

  const formatLeadsWithPercentage = (value: number, total: number): React.ReactElement => {
    const percentage = total > 0 ? (value / total) * 100 : 0;
    return (
      <>
        <strong>{formatNumber(value, 0)}</strong> | {formatNumber(percentage, 2)}%
      </>
    );
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Box sx={{ margin: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <h2>VNR ROI nach Asset Type</h2>
        <Button
          variant="contained"
          color="primary"
          onClick={() => exportToExcel(allData, 'VNR_ROI_AssetType')}
        >
          Export to Excel
        </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'asset_type'}
                  direction={orderBy === 'asset_type' ? order : 'asc'}
                  onClick={(event) => handleRequestSort(event, 'asset_type')}
                >
                  Asset Type
                  {orderBy === 'asset_type' ? (
                    <Box component="span" sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'leads'}
                  direction={orderBy === 'leads' ? order : 'asc'}
                  onClick={(event) => handleRequestSort(event, 'leads')}
                >
                  Leads
                  {orderBy === 'leads' ? (
                    <Box component="span" sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell>ROI (30d)</TableCell>
              <TableCell>ROI (laufend)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData.map((row, index) => {
              const totalCost = parseFloat(String(row.total_cost)) || 0;
              const revenue30d = parseFloat(String(row.revenue_30d)) || 0;
              const revenueTotal = parseFloat(String(row.revenue_total)) || 0;
              const roi30d = totalCost > 0 ? (revenue30d / totalCost) * 100 : 0;
              const roiTotal = totalCost > 0 ? (revenueTotal / totalCost) * 100 : 0;

              return (
                <TableRow key={index}>
                  <TableCell>{row.asset_type}</TableCell>
                  <TableCell>{formatLeadsWithPercentage(row.leads, summary.leads)}</TableCell>
                  <TableCell>{formatNumber(roi30d, 2)}%</TableCell>
                  <TableCell>{formatNumber(roiTotal, 2)}%</TableCell>
                </TableRow>
              );
            })}
            {/* Summary row */}
            {(() => {
              const totalCost = parseFloat(String(summary.total_cost)) || 0;
              const revenue30d = parseFloat(String(summary.revenue_30d)) || 0;
              const revenueTotal = parseFloat(String(summary.revenue_total)) || 0;
              const roi30d = totalCost > 0 ? (revenue30d / totalCost) * 100 : 0;
              const roiTotal = totalCost > 0 ? (revenueTotal / totalCost) * 100 : 0;

              return (
                <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.1)', fontWeight: 'bold' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>{summary.asset_type}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    {formatNumber(summary.leads, 0)}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{formatNumber(roi30d, 2)}%</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{formatNumber(roiTotal, 2)}%</TableCell>
                </TableRow>
              );
            })()}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default RoiPerAssetType;
